import { useMemo } from "react"
import { HASH_INTRINSIC, WORDMARK_INTRINSIC } from "@/lib/brand-assets"

type PlantSprite = {
  left: string
  top: string
  width: number
  rotation: number
  opacity: number
  blur: number
  src: string
}

function createSeededRandom(seed: number) {
  let value = seed % 2147483647

  if (value <= 0) {
    value += 2147483646
  }

  return () => {
    value = (value * 16807) % 2147483647
    return (value - 1) / 2147483646
  }
}

function generatePlants(count: number): PlantSprite[] {
  const random = createSeededRandom(42069)

  return Array.from({ length: count }, (_, index) => {
    const column = index % 5
    const row = Math.floor(index / 5)
    const baseLeft = 8 + column * 21
    const baseTop = 4 + row * 14

    return {
      left: `${Math.min(96, Math.max(0, baseLeft + (random() - 0.5) * 12))}%`,
      top: `${Math.min(96, Math.max(0, baseTop + (random() - 0.5) * 10))}%`,
      width: index % 3 === 0 ? 150 + random() * 90 : 70 + random() * 90,
      rotation: -38 + random() * 76,
      opacity: 0.035 + random() * 0.085,
      blur: random() > 0.75 ? 1.5 + random() * 2 : 0,
      src:
        index % 3 === 0
          ? "/assets/brand/wordmark.svg"
          : "/assets/brand/hash.svg",
    }
  })
}

export function BackgroundPlants() {
  const plants = useMemo(() => generatePlants(28), [])

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,197,94,0.06),transparent_40%),radial-gradient(circle_at_bottom,rgba(34,197,94,0.04),transparent_35%)] dark:bg-[radial-gradient(circle_at_top,rgba(34,197,94,0.10),transparent_40%),radial-gradient(circle_at_bottom,rgba(34,197,94,0.06),transparent_35%)]" />

        {plants.map((plant, index) => {
          const intrinsic = plant.src.includes("wordmark")
            ? WORDMARK_INTRINSIC
            : HASH_INTRINSIC
          const height = Math.round(
            (plant.width * intrinsic.height) / intrinsic.width
          )

          return (
            <div
              key={index}
              className="absolute bg-contain bg-center bg-no-repeat select-none"
              style={{
                left: plant.left,
                top: plant.top,
                width: `${Math.round(plant.width)}px`,
                height: `${height}px`,
                backgroundImage: `url(${plant.src})`,
                opacity: plant.opacity,
                filter: plant.blur ? `blur(${plant.blur}px)` : undefined,
                transform: `rotate(${plant.rotation}deg)`,
              }}
            />
          )
        })}
      </div>
    </>
  )
}

export default BackgroundPlants
